import { useEffect, useMemo, useState } from "react";
import useReveal from "./useReveal";
import { FLOW_PER_MONTH, MILLION_REGIONS, REGIONS, TOPICS } from "./data";
import { pluralRu } from "./siteConfig";

const TOPICS_VISIBLE = 12;
const TOP5 = TOPICS.slice(0, 5).map((t) => t[0]);
const TOTAL_REGION = REGIONS.reduce((s, r) => s + r[1], 0);
const START_TOPICS = ["Гражданское право", "Семейное право", "Трудовое право"];

const nf = (n) => Math.round(n).toLocaleString("ru-RU");

const Constructor = () => {
    const [ref, visible] = useReveal();
    const [mode, setMode] = useState("target");
    const [topicsChecked, setTopicsChecked] = useState(() => {
        const obj = {};
        START_TOPICS.forEach((n) => {
            obj[n] = true;
        });
        return obj;
    });
    const [prices, setPrices] = useState({});
    const [regionsChecked, setRegionsChecked] = useState({});
    const [expanded, setExpanded] = useState(false);
    const [geoQuery, setGeoQuery] = useState("");
    const [volume, setVolume] = useState(300);

    useEffect(() => {
        const handler = (e) => setMode(e.detail);
        window.addEventListener("lp:set-mode", handler);
        return () => window.removeEventListener("lp:set-mode", handler);
    }, []);

    const flow = mode === "flow";

    const setRegionsFromList = (list) => {
        const obj = {};
        list.forEach((name) => {
            obj[name] = true;
        });
        setRegionsChecked(obj);
    };

    const selectedTopics = useMemo(
        () => TOPICS.filter(([name]) => topicsChecked[name]),
        [topicsChecked]
    );
    const selectedRegions = useMemo(
        () => REGIONS.filter(([name]) => regionsChecked[name]),
        [regionsChecked]
    );

    const regSum = selectedRegions.length
        ? selectedRegions.reduce((s, r) => s + r[1], 0)
        : TOTAL_REGION;
    const share = regSum / TOTAL_REGION;
    const avail = flow
        ? FLOW_PER_MONTH * share
        : selectedTopics.reduce((s, t) => s + t[1], 0) * share;
    const want = volume;

    const sumAvailText = !flow && !selectedTopics.length ? "—" : `~${nf(avail)}`;
    const sumTopicsText = flow ? "все 30" : selectedTopics.length || "не выбраны";
    const sumGeoText = selectedRegions.length
        ? `${selectedRegions.length} ${pluralRu(selectedRegions.length, "регион", "региона", "регионов")}`
        : "Вся Россия";
    const priced = selectedTopics.filter(([name]) => prices[name]);
    const sumPriceText = priced.length ? `по ${priced.length} из ${selectedTopics.length}` : "нет";

    let barWidth = 0;
    let verdictClass = "sum__verdict";
    let verdictText = "";
    let label = "заявок в месяц в выбранных тематиках";

    if (flow) {
        label = "заявок в месяц — весь поток региона";
        barWidth = Math.min(100, avail ? (want / avail) * 100 : 0);
        if (avail >= want) {
            verdictText = `Поток выбранных регионов — около ${nf(avail)} заявок в месяц, ваш запрос на ${nf(want)} он закрывает полностью.`;
        } else {
            verdictClass = "sum__verdict is-warn";
            verdictText = `Поток выбранных регионов — около ${nf(avail)} заявок в месяц, это меньше запрошенных ${nf(want)}. Добавьте регионы, чтобы набрать объём.`;
        }
    } else if (!selectedTopics.length) {
        verdictText = "Выберите хотя бы одну тематику — покажем, сколько заявок в ней приходит.";
    } else {
        barWidth = Math.min(100, avail ? (want / avail) * 100 : 0);
        if (avail >= want) {
            verdictText = `В выбранных тематиках и регионах приходит около ${nf(avail)} заявок в месяц. Сколько из них достанется вам — зависит от ваших ставок: чем они выше, тем больше лидов выигрывает ваша кампания.`;
        } else {
            verdictClass = "sum__verdict is-warn";
            verdictText = `В выбранных тематиках и регионах приходит около ${nf(avail)} заявок в месяц — меньше запрошенных ${nf(want)}, и это весь поток ниши, а не ваша гарантированная доля. Добавьте тематики или регионы, либо поднимите ставки.`;
        }
    }

    const visibleTopics = expanded ? TOPICS : TOPICS.slice(0, TOPICS_VISIBLE);
    const q = geoQuery.trim().toLowerCase();
    const visibleRegions = REGIONS.filter(([name]) => !q || name.toLowerCase().includes(q));

    return (
        <section
            className={`sect sect--dark reveal${visible ? " is-in" : ""}`}
            id="constructor"
            ref={ref}
        >
            <div className="wrap">
                <div className="sect__head sect__head--light">
                    <span className="eyebrow eyebrow--light">Конструктор заявки</span>
                    <h2 className="sect__title">Соберите свой поток заявок для юристов за минуту</h2>
                    <p className="sect__lead">
                        Выберите формат, тематики, регионы и объём — мы посчитаем доступное
                        количество заявок. Объёмы — реальная статистика нашей базы за 12 месяцев.
                    </p>
                </div>

                <div className="cons">
                    <div className="cons__main">
                        <div className="cstep">
                            <div className="cstep__head">
                                <span className="cstep__n">1</span>
                                <h3>Формат закупки</h3>
                            </div>
                            <div className="modes">
                                <label className="mode">
                                    <input
                                        type="radio"
                                        name="mode"
                                        value="flow"
                                        checked={mode === "flow"}
                                        onChange={() => setMode("flow")}
                                    />
                                    <span className="mode__box">
                                        <b>Общий поток на регион</b>
                                        <em>Все тематики, фиксированная цена, без отбраковки</em>
                                    </span>
                                </label>
                                <label className="mode">
                                    <input
                                        type="radio"
                                        name="mode"
                                        value="target"
                                        checked={mode === "target"}
                                        onChange={() => setMode("target")}
                                    />
                                    <span className="mode__box">
                                        <b>Аукцион по тематикам</b>
                                        <em>Свои направления и ставки, отбраковка</em>
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div className={`cstep${flow ? " is-flow" : ""}`} id="step-topics">
                            <div className="cstep__head">
                                <span className="cstep__n">2</span>
                                <h3>
                                    Тематики{" "}
                                    <small>
                                        {flow
                                            ? "— в общий поток входят все направления"
                                            : "и ваша ставка за лид, ₽"}
                                    </small>
                                </h3>
                                {!flow && (
                                    <button
                                        type="button"
                                        className="linkbtn"
                                        onClick={() => {
                                            const obj = {};
                                            TOPICS.forEach(([name]) => {
                                                obj[name] = TOP5.includes(name);
                                            });
                                            setTopicsChecked(obj);
                                        }}
                                    >
                                        Выбрать топ-5
                                    </button>
                                )}
                            </div>
                            {flow && (
                                <div className="flownote">
                                    В формате «Общий поток» тематики не выбираются — вы получаете
                                    все направления региона сразу и по фиксированной цене. Ниже
                                    показано, из чего состоит поток.
                                </div>
                            )}
                            {!flow && (
                                <p className="cstep__note">
                                    Ставка — это не потолок, а ваша позиция в аукционе. Лид уходит
                                    тому, кто предложил больше: чем выше ставка по тематике, тем
                                    чаще заявки в ней достаются вам. Ставку можно менять в любой
                                    момент.
                                </p>
                            )}
                            <div className="topics">
                                {visibleTopics.map(([name, vol]) => {
                                    const checked = flow || !!topicsChecked[name];
                                    const on = flow || checked;
                                    return (
                                        <label
                                            key={name}
                                            className={`topic${on ? " is-on" : ""}${!flow ? " is-target" : ""}`}
                                        >
                                            {!flow && (
                                                <input
                                                    type="checkbox"
                                                    checked={!!topicsChecked[name]}
                                                    onChange={() =>
                                                        setTopicsChecked((prev) => ({
                                                            ...prev,
                                                            [name]: !prev[name],
                                                        }))
                                                    }
                                                />
                                            )}
                                            <span className="topic__name">{name}</span>
                                            <span className="topic__vol">~{nf(vol)} / мес</span>
                                            {!flow && (
                                                <span className="topic__price">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="10"
                                                        placeholder="ставка"
                                                        aria-label={`Ваша ставка за лид, ${name}`}
                                                        value={prices[name] || ""}
                                                        onChange={(e) =>
                                                            setPrices((prev) => ({
                                                                ...prev,
                                                                [name]: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                    <span>₽ — ваша ставка за лид</span>
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                            <button
                                type="button"
                                className="linkbtn linkbtn--more"
                                onClick={() => setExpanded((v) => !v)}
                            >
                                {expanded ? "Свернуть список" : `Показать все ${TOPICS.length} тематик`}
                            </button>
                        </div>

                        <div className="cstep">
                            <div className="cstep__head">
                                <span className="cstep__n">3</span>
                                <h3>География</h3>
                                <button
                                    type="button"
                                    className="linkbtn"
                                    onClick={() => setRegionsFromList([])}
                                >
                                    Вся Россия
                                </button>
                            </div>
                            <p className="cstep__note">
                                Закупка идёт регионами целиком: отметьте нужные субъекты РФ.
                                Выбрать отдельный город внутри региона нельзя — заявки приходят по
                                всему региону.
                            </p>
                            <div className="geo__search">
                                <input
                                    type="search"
                                    placeholder="Найти регион…"
                                    autoComplete="off"
                                    value={geoQuery}
                                    onChange={(e) => setGeoQuery(e.target.value)}
                                />
                            </div>
                            <div className="geo__list">
                                {visibleRegions.map(([name, vol]) => (
                                    <label
                                        key={name}
                                        className={`geo${regionsChecked[name] ? " is-on" : ""}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!regionsChecked[name]}
                                            onChange={() =>
                                                setRegionsChecked((prev) => ({
                                                    ...prev,
                                                    [name]: !prev[name],
                                                }))
                                            }
                                        />
                                        <span className="geo__name">{name}</span>
                                        <span className="geo__vol">~{nf(vol)} / мес</span>
                                    </label>
                                ))}
                            </div>
                            {visibleRegions.length === 0 && (
                                <p className="geo__empty">
                                    Регион не найден. Проверьте написание — в списке все 83
                                    региона, откуда за последний год приходили заявки.
                                </p>
                            )}
                            <div className="geo__quick">
                                <button
                                    type="button"
                                    className="chipbtn"
                                    onClick={() => setRegionsFromList(MILLION_REGIONS)}
                                >
                                    Регионы с миллионниками
                                </button>
                                <button
                                    type="button"
                                    className="chipbtn"
                                    onClick={() =>
                                        setRegionsFromList(REGIONS.slice(0, 10).map((r) => r[0]))
                                    }
                                >
                                    Топ-10 по объёму
                                </button>
                                <button
                                    type="button"
                                    className="chipbtn"
                                    onClick={() => setRegionsFromList([])}
                                >
                                    Очистить
                                </button>
                            </div>
                        </div>

                        <div className="cstep">
                            <div className="cstep__head">
                                <span className="cstep__n">4</span>
                                <h3>Нужный объём в месяц</h3>
                            </div>
                            <div className="range">
                                <input
                                    type="range"
                                    min="50"
                                    max="3000"
                                    step="50"
                                    value={volume}
                                    onChange={(e) => setVolume(Number(e.target.value))}
                                />
                                <output>{nf(volume)} лидов</output>
                            </div>
                        </div>
                    </div>

                    <aside className="cons__side">
                        <div className="sum">
                            <div className="sum__title">Ваш поток</div>
                            <div className="sum__big">
                                <span>{sumAvailText}</span>
                                <small>{label}</small>
                            </div>
                            <div className="sum__bar">
                                <i style={{ width: `${barWidth}%` }}></i>
                            </div>
                            <p className={verdictClass}>{verdictText}</p>
                            <dl className="sum__rows">
                                <div>
                                    <dt>Формат</dt>
                                    <dd>{flow ? "Общий поток на регион" : "Аукцион по тематикам"}</dd>
                                </div>
                                <div>
                                    <dt>Тематик выбрано</dt>
                                    <dd>{sumTopicsText}</dd>
                                </div>
                                <div>
                                    <dt>Регионов</dt>
                                    <dd>{sumGeoText}</dd>
                                </div>
                                <div>
                                    <dt>Запрошенный объём</dt>
                                    <dd>{nf(volume)} / мес</dd>
                                </div>
                                {!flow && (
                                    <div>
                                        <dt>Ставки указаны</dt>
                                        <dd>{sumPriceText}</dd>
                                    </div>
                                )}
                            </dl>
                            <a href="#order" className="btn btn--block btn--lg">
                                Войти в кабинет
                            </a>
                            <p className="sum__hint">
                                Настройки конструктора можно будет повторить в личном кабинете
                                после входа или регистрации.
                            </p>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
};

export default Constructor;
